<template>
  <div class="container">
    <div v-if="loading" class="d-flex justify-content-center align-items-center" style="height: 350px;">
      <LoadingSpinner />
    </div>
    <div v-else>
      <!-- Filtros en card amplio -->
      <div class="mb-3">
        <div class="body">
          <div class="d-flex flex-row justify-content-between">
            <h3 class="font-weight-bold">Analyse Vocales</h3>
            <h5 class="float-right font-weight-bold">Note Globale: {{ totalEvaluations }} / 100</h5>
          </div>
          <div class="form-row align-items-center">
            <div class="col-sm-3 mb-2 mb-sm-0">
              <date-picker
                v-model="range.startDate"
                class="form-control"
                :config="datePickerConfig"
              />
            </div>
            <div class="col-sm-3 mb-2 mb-sm-0">
              <date-picker
                v-model="range.endDate"
                class="form-control"
                :config="datePickerConfigEndDate"
              />
            </div>
            <div class="col-sm-2 text-sm-right">
              <button class="btn btn-success btn-block" @click="requestData">
                <i class="icon-calendar-check-o mr-2"></i>
                <small>{{ $t('lang.button.filter') }}</small>
              </button>
            </div>
          </div>
        </div>
      </div>
  
      <div class="row">
        <div class="col-md-12">
          <div class="card" style="min-height: 350px; min-width: 650px">
            <div class="p-2 d-flex justify-content-between align-items-center">
              <div class="btn-group" role="group">
                <button
                  type="button"
                  class="btn btn-outline-primary"
                  :class="{ active: mode === 'day' }"
                  @click="setMode('day')"
                >
                  <small>{{ $t('lang.label.days') }}</small>
                </button>
                <button
                  type="button"
                  class="btn btn-outline-primary"
                  :class="{ active: mode === 'week' }"
                  @click="setMode('week')"
                >
                  <small>{{ $t('lang.label.weeks') }}</small>
                </button>
                <button
                  type="button"
                  class="btn btn-outline-primary"
                  :class="{ active: mode === 'month' }"
                  @click="setMode('month')"
                >
                  <small>{{ $t('lang.label.months') }}</small>
                </button>
              </div>
            </div>
            <div class="card-body p-1">
              <div class="mt-3">
                <line-chart
                  v-if="chartModel.datasets.length > 0"
                  :width="400"
                  :height="300"
                  :chart-data="chartModel"
                  :options="chartModel.options"
                />
              </div>
            </div>
  
            <div class="px-3 pb-3">
              <div class="d-flex flex-row">
                <div
                  v-for="(opt, idx) in optionsEvaluationScore"
                  :key="opt.id"
                  class="d-flex align-items-center mb-2"
                >
                  <span
                    class="legend-color-dot mr-1 ml-3"
                    :style="{ backgroundColor: seriesColors[idx % seriesColors.length] }"
                  />
                  <small class="text-muted">{{ opt.label }}</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="row">
        <div class="col-md-12">
          <BookerDailyReport :selectedUserId="selectedUserId" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { bookersService } from "@/_services";
import LineChart from '@/components/Chart.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import BookerDailyReport from '@/components/BDR/BookerDailyReport.vue';
import moment from "moment";

export default {
  name: 'UserBookerEvaluations',
  components: { 
    LineChart, 
    LoadingSpinner, 
    BookerDailyReport 
  },
  props: {
    // Datos ya agregados por fecha en el eje tiempo (Y) y porcentajes por opción en X
    // Formato: [{ date: 'YYYY-MM-DD', percentagesByOptionId: { [id:number]: number } }]
    selectedUserId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      mode: 'day',
      buckets: [],
      loading: false,
      datePickerConfig: {
        format: "YYYY-MM-DD",
        useCurrent: false,
        // minDate: moment().startOf("day")
        maxDate: moment().endOf("day"),
      },
      datePickerConfigEndDate: {
        format: "YYYY-MM-DD",
        useCurrent: false,
        maxDate: moment().endOf("day"),
      },
      range: {
        startDate: moment().subtract(1, "month").format("YYYY-MM-DD"),
        endDate: moment().format("YYYY-MM-DD"),
      },
      seriesColors: ['#1abc9c', '#e74c3c', '#3498db', '#9b59b6', '#f1c40f', '#2ecc71'],
      optionsEvaluationScore: [
        { id: 1, label: this.$t("lang.label.eval_task_fake_1").replace(/\s*\(.*/, '').trim() },
        { id: 2, label: this.$t("lang.label.eval_task_fake_2").replace(/\s*\(.*/, '').trim() },
        { id: 3, label: this.$t("lang.label.eval_task_fake_3").replace(/\s*\(.*/, '').trim() },
        { id: 4, label: this.$t("lang.label.eval_task_fake_4").replace(/\s*\(.*/, '').trim() },
        { id: 5, label: this.$t("lang.label.eval_task_fake_5").replace(/\s*\(.*/, '').trim() },
        { id: 6, label: this.$t("lang.label.eval_task_fake_6").replace(/\s*\(.*/, '').trim() },
      ],
    }
  },
  computed: {

    groupedBuckets() {
    if (!this.buckets || this.buckets.length === 0) {
      return []
    }

    if (this.mode === 'day') {
      // Se usan los datos tal cual
      return this.buckets
    }

    const grouped = {}

    this.buckets.forEach(item => {
      const date = moment(item.date, 'YYYY-MM-DD')

      let key = ''
      if (this.mode === 'week') {
        const week = date.isoWeek()
        const year = date.isoWeekYear()
        key = `${year}-W${week}`
      } else if (this.mode === 'month') {
        key = date.format('YYYY-MM')
      }

      if (!grouped[key]) {
        grouped[key] = {
          date: key,
          percentagesByOptionId: {}
        }
      }

      Object.entries(item.percentagesByOptionId).forEach(([optId, value]) => {
        if (!grouped[key].percentagesByOptionId[optId]) {
          grouped[key].percentagesByOptionId[optId] = 0
        }
        grouped[key].percentagesByOptionId[optId] += value
      })

      // Podés guardar un contador si querés promediar
      if (!grouped[key].count) grouped[key].count = 0
      grouped[key].count++
    })

    // Promediar los valores (igual que hacía tu watcher)
    return Object.values(grouped).map(item => {
      const normalized = { date: item.date, percentagesByOptionId: {} }
      Object.entries(item.percentagesByOptionId).forEach(([optId, value]) => {
        normalized.percentagesByOptionId[optId] = value / item.count
      })
      return normalized
    })
  },

  // tu chartModel ahora usa groupedBuckets en lugar de buckets
  chartModel() {
    const dataSource = this.groupedBuckets

    const datasets = this.optionsEvaluationScore.map((opt, idx) => ({
      label: opt.label,
      borderColor: this.seriesColors[idx % this.seriesColors.length],
      backgroundColor: 'transparent',
      fill: false,
      lineTension: 0.2,
      data: dataSource.map(b => ({
        x: b.date,
        y: (b && b.percentagesByOptionId && b.percentagesByOptionId[opt.id] != null)
          ? b.percentagesByOptionId[opt.id]
          : 0
      }))
    }))

    const timeUnit = this.mode === 'day' ? 'day' : this.mode === 'week' ? 'week' : 'month'

    return {
      datasets,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false, position: 'right' },
        tooltips: {
          mode: 'nearest',
          callbacks: {
            label: (tooltipItem, data) => {
              const ds = data.datasets[tooltipItem.datasetIndex]
              return `${ds.label}: ${tooltipItem.yLabel}%`
            }
          }
        },
        scales: {
          xAxes: [
            {
              type: 'time',
              time: { unit: timeUnit, tooltipFormat: 'YYYY-MM-DD' },
              distribution: 'series',
              ticks: { callback: v => this.formatDateLabel(v) },
              scaleLabel: { display: true }
            }
          ],
          yAxes: [
            {
              type: 'linear',
              ticks: { min: 0, max: 100, callback: v => `${v}%` },
              scaleLabel: { display: true }
            }
          ]
        }
      }
    }
  },

    totalEvaluations() {
      const data = this.groupedBuckets
      if (!data || data.length === 0) return 0

      let sum = 0
      let count = 0
      data.forEach(item => {
        Object.values(item.percentagesByOptionId).forEach(value => {
          sum += value
          count++
        })
      })
      return parseFloat(((sum / count) || 0).toFixed(1))
    }
  },
  created(){
    this.requestData();
  },
  methods: {
  formatDateLabel(dateStr) {
    let fullDateStr = dateStr
    
    // Si el string no tiene coma + año, lo agregamos
    if (!/\d{4}$/.test(dateStr)) {  
      // Agregar el año actual
      const currentYear = new Date().getFullYear()
      fullDateStr = `${dateStr}, ${currentYear}`
    }

    const date = new Date(fullDateStr)
    console.log(fullDateStr, date)

    if (this.mode === 'week') {
      // Mostrar "YYYY Week N"
      const year = date.getFullYear()
      const onejan = new Date(date.getFullYear(), 0, 1)
      const week = Math.ceil((((date - onejan) / 86400000) + onejan.getDay() + 1) / 7)
      return `${year} Week ${week}`
    } else if (this.mode === 'month') {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, "0")
      return `${year}-${month}`
    } else {
      return dateStr  // día
    }
  },
    setMode(m) {
      if (this.mode !== m) {
        this.mode = m;
        this.requestData();
      }
    },
    requestData() {
      if (!this.selectedUserId || !this.range.startDate || !this.range.endDate) {
        this.buckets = []
        return
      }
      this.loading = true;
      bookersService.getScoreOptions(
        this.selectedUserId,
        this.range.startDate,
        this.range.endDate
      )
      .then((response) => {
        const result = (response && response.result) ? response.result : []
        // Ensure expected shape for the chart
        this.buckets = result
          .filter(item => item && item.date && item.percentagesByOptionId)
          .map(item => ({
            date: item.date,
            percentagesByOptionId: item.percentagesByOptionId
          }))
      })
      .catch(() => {
        this.buckets = []
      })
      .finally(() => {
        this.loading = false;
      });
    }
  }
}
</script>

<style scoped>
.legend-color-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
}
</style>